import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import DataTable, { Column } from '../../components/DataTable';
import { useCrudApi } from '../../hooks/useApi';
import apiService from '../../services/apiService';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CreditCard, Plus, Search, Download,
  CheckCircle2, Clock, XCircle, AlertTriangle, User, FileText,
} from 'lucide-react';

interface Payment {
  _id: string;
  paymentNumber: string;
  amount: number;
  currency: string;
  paymentDate: string;
  paymentMethod: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  invoiceId?: { _id: string; invoiceNumber: string; clientId?: { firstName: string; lastName: string } };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const PaymentManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ⬇️ Normalisation stricte pour renvoyer { items, total } au hook
  const paymentApiConfig = useMemo(() => ({
    getAll: async (params: any) => {
      const resp = await apiService.payments.getAll(params);
      const items =
        resp?.items ??              // fallback si apiService a déjà « déballé »
        [];
      const total =
        resp?.total ??
        (Array.isArray(items) ? items.length : 0);
      return { items, total };
    },
    getById: apiService.payments.getById as any,
    create: apiService.payments.create as any,
    update: apiService.payments.update as any,
    delete: apiService.payments.delete as any,
  }), []);

  const {
    items: payments,
    total = 0,
    isLoading,
    fetchItems: fetchPayments,
    deleteItem: deletePayment,
  } = useCrudApi<Payment, any, any>(paymentApiConfig);

  useEffect(() => {
    fetchPayments({
      page: currentPage,
      limit: pageSize,
      status: statusFilter === 'all' ? '' : statusFilter,
      search: searchTerm,
      method: typeFilter === 'all' ? '' : typeFilter,
    });
  }, [fetchPayments, currentPage, pageSize, statusFilter, searchTerm, typeFilter]);

  const handleDownloadPdf = async (paymentId: string) => {
    if (!paymentId) {
      toast.error('ID de paiement manquant pour le téléchargement du PDF.');
      return;
    }
    try {
      const response = await apiService.payments.downloadPdf(paymentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paiement-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF.');
    }
  };

  const getStatusBadge = (status: Payment['status']) => {
    const statusConfig = {
      completed: { color: 'bg-success-100 text-success-800', label: 'Terminé', icon: CheckCircle2 },
      pending:   { color: 'bg-warning-100 text-warning-800', label: 'En attente', icon: Clock },
      failed:    { color: 'bg-error-100 text-error-800', label: 'Échoué', icon: XCircle },
      refunded:  { color: 'bg-gray-100 text-gray-800',   label: 'Remboursé', icon: AlertTriangle },
    } as const;
    const cfg = statusConfig[status] ?? statusConfig.pending;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
        <Icon className="w-3 h-3 mr-1" />{cfg.label}
      </span>
    );
  };

  const columns: Column<Payment>[] = [
    { header: 'N° Paiement', accessor: 'paymentNumber' },
    {
      header: 'Client',
      accessor: (p) => p.invoiceId?.clientId?.firstName || 'Client inconnu',
      cell: (p) => (
        <div className="flex items-center text-sm">
          <User className="w-4 h-4 mr-2 text-gray-400" />
          <span>
            {p.invoiceId?.clientId
              ? `${p.invoiceId.clientId.firstName} ${p.invoiceId.clientId.lastName}`
              : 'Client inconnu'}
          </span>
        </div>
      ),
    },
    {
      header: 'Montant',
      accessor: 'amount',
      cell: (p) =>
        (typeof p.amount === 'number'
          ? p.amount
          : Number(p.amount || 0)
        ).toLocaleString('fr-FR', { style: 'currency', currency: (p.currency || 'EUR') }),
    },
    { header: 'Date', accessor: 'paymentDate', cell: (p) => new Date(p.paymentDate).toLocaleDateString('fr-FR') },
    { header: 'Statut', accessor: 'status', cell: (p) => getStatusBadge(p.status) },
    {
      header: 'Facture liée',
      accessor: (p) => p.invoiceId?.invoiceNumber || 'N/A',
      cell: (p) => (
        <div className="flex items-center text-sm">
          {p.invoiceId ? (
            <a href={`/admin/invoices/view/${p.invoiceId._id}`} className="text-primary-600 hover:underline flex items-center">
              <FileText className="w-4 h-4 mr-2 text-gray-400" />
              <span>{p.invoiceId.invoiceNumber}</span>
            </a>
          ) : (
            <span className="text-gray-600 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-gray-400" />
              <span>N/A</span>
            </span>
          )}
        </div>
      ),
    },
    { header: 'Méthode', accessor: 'paymentMethod' },
  ];

  // Ceinture de sécurité si l’API renvoie undefined
  const safePayments = Array.isArray(payments) ? payments : [];

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <CreditCard className="w-8 h-8 mr-3 text-primary-600" />
              Gestion des Paiements
            </h1>
            <p className="text-gray-600 mt-2">Suivez les paiements liés aux factures et transactions</p>
          </div>
          <Button size="lg" onClick={() => navigate('/admin/payments/add')} className="flex items-center">
            <Plus className="w-5 h-5 mr-2" />Nouveau Paiement
          </Button>
        </div>

        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par client, numéro ou facture..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </Card>

        <DataTable
          columns={columns}
          data={safePayments}
          keyField="_id"
          isLoading={isLoading}
          pagination={{ page: currentPage, limit: pageSize, total, onPageChange: setCurrentPage, onLimitChange: setPageSize }}
          actions={{
            view:  (p) => navigate(`/admin/payments/view/${p._id}`),
            edit:  (p) => navigate(`/admin/payments/edit/${p._id}`),
            delete:(p) => deletePayment(p._id),
            custom: [
              {
                label: 'Télécharger PDF',
                icon: <Download className="w-4 h-4" />,
                onClick: (_e, p) => handleDownloadPdf(p._id),
              },
            ],
          }}
          searchPlaceholder="Rechercher par N° paiement, client ou facture..."
          onSearch={setSearchTerm}
          emptyMessage="Aucun paiement trouvé"
        />
      </div>
    </Layout>
  );
};

export default PaymentManagement;